<div class="flex flex-col gap-8">
  <div class="header">
    <h2 class="text-gray-12 text-2xl font-semibold">Présentation du bien</h2>
    <p class="text-gray-11 mt-1 text-sm">
      Donnez envie aux futurs acheteurs avec un titre percutant et une description détaillée.
    </p>
  </div>
  <div class="gap-6 flex flex-col">
    <!-- Tips Section -->
    <div class="rounded-xl border border-gray-6 bg-gray-2 p-4">
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-gray-12">Mettez en avant ce qui rend votre bien unique.</span>
        <button type="button" (click)="toggleTips()" class="text-sm font-medium text-primary hover:underline">
          @if (showTips()) {
            Masquer les conseils
          } @else {
            Obtenir des conseils
          }
        </button>
      </div>

      <div class="mt-4 space-y-10 text-sm text-gray-11 border-t border-gray-6 pt-6">
        <!-- Avertissement Informations Personnelles / CGU -->
        <div class="rounded-lg bg-red-3 p-4 border border-red-5 text-red-11">
          <h4 class="font-semibold flex items-center gap-2 text-sm text-red-12">
            <mat-icon class="text-red-9 text-[20px] w-5 h-5 align-middle mb-0.5">gavel</mat-icon>
            Rappel des CGU — Coordonnées personnelles strictement interdites
          </h4>
          <p class="mt-1 text-xs text-red-11 leading-relaxed">
            Conformément aux <strong>Conditions Générales d'Utilisation (CGU)</strong> de la plateforme, il
            est strictement interdit d'inclure des coordonnées personnelles ou directes dans le titre ou la
            description de votre bien (numéro de téléphone, adresse email, réseaux sociaux, liens externes,
            nom ou prénom). Les premiers contacts et échanges entre acquéreurs et vendeurs doivent s'effectuer
            exclusivement via la messagerie sécurisée du site. Tout contenu non conforme sera automatiquement
            filtré ou supprimé.
          </p>
        </div>

        @if (showTips()) {
          <!-- SECTION TITRE -->
          <div class="space-y-6">
            <h3 class="text-lg font-bold text-gray-12 border-b border-gray-5 pb-2">Choix du titre</h3>
            <p>
              Le titre de votre annonce est souvent la première chose que les agents immobiliers et acquéreurs
              lisent lorsqu'ils voient votre bien. C'est l'occasion de mettre en avant ce qui rend votre bien
              unique, de susciter l'intérêt des acquéreurs et de leur permettre de mieux se le représenter.
            </p>

            <div>
              <h4 class="font-semibold text-gray-12 mb-1">Choisissez un titre court</h4>
              <p>
                Réfléchissez à ce qui rend votre bien attrayant et le distingue des autres. N'essayez pas de
                tout dire : invitez simplement les acquéreurs à en savoir plus.
              </p>
              <p class="mt-2">
                Commencez par les informations les plus importantes et essayez d'utiliser moins de 50
                caractères. Les titres longs sont automatiquement tronqués dans les résultats de recherche
                pour s'adapter aux petits écrans. Par exemple, le titre « Maison rurale avec vue sur le lac,
                terrasse, garage double » peut apparaître sous la forme « Maison rurale avec vue sur le lac…
                ».
              </p>
              <p class="mt-3 font-medium text-gray-12">Voici 3 bons exemples de titres d'annonce abrégés :</p>
              <ul class="list-disc pl-5 mt-1 space-y-1">
                <li>Maison d'architecte pour amateurs d'espaces verts</li>
                <li>Loft moderne avec vue sur la ville</li>
                <li>Villa contemporaine près du centre-ville</li>
              </ul>
            </div>

            <div>
              <h4 class="font-semibold text-gray-12 mb-1">Écrivez la plupart des mots en minuscules</h4>
              <p>
                N'utilisez de majuscule que sur la première lettre du premier mot du titre. Les minuscules ont
                tendance à être plus faciles à lire et plus conviviales que les majuscules.
              </p>
              <p class="mt-2">
                Vous pouvez faire des exceptions pour les noms propres, tels que le nom d'un quartier ou d'un
                monument.
              </p>
              <p class="mt-3 font-medium text-gray-12">
                Voici 3 bons exemples de titres d'annonce avec majuscules :
              </p>
              <ul class="list-disc pl-5 mt-1 space-y-1">
                <li>Maison en bord de mer à La Baule</li>
                <li>Appartement haussmannien style Louis XVI</li>
                <li>Studio éco-responsable près des commerces</li>
              </ul>
            </div>

            <div>
              <h4 class="font-semibold text-gray-12 mb-1">Évitez les symboles et les émojis</h4>
              <p>
                Les émojis et les symboles ne sont pas interprétés de la même manière à travers le monde, et
                peuvent donc être déroutants ou trompeurs. L'utilisation de ces caractères spéciaux peut
                rendre votre titre inaccessible à certains acquéreurs. Utilisez plutôt des mots simples et
                descriptifs.
              </p>
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <div class="rounded bg-red-3 p-3 text-red-11 border border-red-5">
                  <p class="text-xs font-semibold mb-1 uppercase tracking-wide">À ne pas faire</p>
                  <p class="font-medium">**** 🏠 Maison avec piscine et jardin 🪏 **** !!!</p>
                </div>
                <div class="rounded bg-green-3 p-3 text-green-11 border border-green-5">
                  <p class="text-xs font-semibold mb-1 uppercase tracking-wide">Meilleure façon</p>
                  <p class="font-medium">Maison avec piscine/jardin clôturé</p>
                </div>
              </div>
            </div>

            <div>
              <h4 class="font-semibold text-gray-12 mb-1">Ajoutez des informations utiles</h4>
              <p>
                Il n'est généralement pas nécessaire de répéter les informations fournies automatiquement aux
                acquéreurs dans les résultats de recherche, notamment la ville et le nombre de pièces.
                Utilisez plutôt le titre de l'annonce pour ajouter plus de détails, comme dans ces deux
                exemples.
              </p>

              <div class="mt-3 space-y-3">
                <div class="p-3 bg-gray-3 rounded-lg border border-gray-5">
                  <p class="text-gray-12">
                    Au lieu de :
                    <span class="text-gray-10 italic line-through">Nouvel appartement T4 à Bordeaux</span>
                  </p>
                  <p class="mt-2 text-xs text-gray-11">
                    Pensez à aider les acquéreurs à mieux comprendre votre emplacement :
                  </p>
                  <p class="font-medium text-primary mt-1">
                    Appartement ensoleillé près de la place des Quinconces
                  </p>
                </div>
                <div class="p-3 bg-gray-3 rounded-lg border border-gray-5">
                  <p class="text-gray-12">
                    Au lieu de :
                    <span class="text-gray-10 italic line-through"
                      >Maison contemporaine récente à Bordeaux</span
                    >
                  </p>
                  <p class="mt-2 text-xs text-gray-11">Pensez à mettre en avant un équipement clé :</p>
                  <p class="font-medium text-primary mt-1">Maison de ville avec studio de musique</p>
                </div>
              </div>
            </div>
          </div>

          <!-- SECTION DESCRIPTION -->
          <div class="space-y-6">
            <h3 class="text-lg font-bold text-gray-12 border-b border-gray-5 pb-2">
              Choix de la description
            </h3>
            <p>
              La description est votre espace pour raconter l'histoire de votre bien. Avec une limite de 800
              caractères, vous avez l'opportunité d'approfondir ce que le titre a suggéré et de répondre aux
              questions que se posent les acquéreurs potentiels en les projetant dans leur futur quotidien.
            </p>

            <div>
              <h4 class="font-semibold text-gray-12 mb-1">
                Mettez en avant la qualité de vie et le quartier
              </h4>
              <p>
                Un acquéreur n'achète pas seulement des murs, mais aussi un mode de vie. Parlez de
                l'environnement immédiat : proximité des commerces, des écoles ou accès aux transports en
                commun. Au contraire, mettez en valeur l'isolement et la tranquillité si c'est le point fort
                de la propriété.
              </p>
              <div class="mt-3 p-3 bg-gray-3 rounded-lg border border-gray-5">
                <p class="font-medium text-primary">
                  « Idéalement situé à 5 minutes à pied du marché couvert et des écoles, dans une rue calme à
                  sens unique. »
                </p>
              </div>
            </div>

            <div>
              <h4 class="font-semibold text-gray-12 mb-1">Soyez transparent sur le confort et le climat</h4>
              <p>
                Mentionnez la luminosité grâce à l'exposition, la qualité de l'isolation, ou le type de
                chauffage. Les acquéreurs sont de plus en plus sensibles aux performances énergétiques et au
                bien-être au quotidien.
              </p>
              <p class="mt-2 text-gray-12 font-medium">Exemple à exploiter :</p>
              <p class="italic text-gray-11 mt-1">
                « Très lumineux grâce à son exposition plein sud, ce bien reste frais en été et conserve une
                chaleur douce en hiver grâce à son excellente isolation et sa pompe à chaleur récente. »
              </p>
            </div>

            <div>
              <h4 class="font-semibold text-gray-12 mb-1">
                Abordez la réalité du bien, sans masquer les nuisances
              </h4>
              <p>
                La transparence instaure la confiance. Si votre bien est au-dessus d'une rue animée ou proche
                d'un lieu de rassemblement, il est préférable de le mentionner habilement plutôt que de le
                cacher. Cela permet de rassurer les acquéreurs et d'éviter les déceptions dommageables lors
                des visites.
              </p>
              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <div class="rounded bg-red-3 p-3 text-red-11 border border-red-5">
                  <p class="text-xs font-semibold mb-1 uppercase tracking-wide">À ne pas faire (cacher)</p>
                  <p class="font-medium sm:text-sm">
                    Passer sous silence un grand boulevard bruyant au pied de l'immeuble.
                  </p>
                </div>
                <div class="rounded bg-green-3 p-3 text-green-11 border border-green-5">
                  <p class="text-xs font-semibold mb-1 uppercase tracking-wide">Meilleure approche</p>
                  <p class="font-medium sm:text-sm">
                    « Situé sur un axe dynamique de la ville, l'appartement bénéficie d'un double vitrage
                    récent garantissant le calme à l'intérieur. »
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 class="font-semibold text-gray-12 mb-1">Structurez et aérez votre texte</h4>
              <p>
                Évitez les pavés de textes indigestes. L'utilisation de petits paragraphes pour aborder
                l'espace jour, l'espace nuit ou l'extérieur facilite grandement la lecture et la clarté.
              </p>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Real-time Personal Contact Warning Alert -->
    @if (hasDetectedContact()) {
      <aura-alert
        [alertInput]="{
          type: auraColorType.WARN,
          title: 'Coordonnées personnelles détectées',
          message:
            'Conformément aux CGU, il est strictement interdit de mentionner des informations de contact directes (téléphone, email, réseaux sociaux, liens...) dans votre annonce. Ces coordonnées seront automatiquement masquées ou supprimées.',
        }"
      ></aura-alert>
    }

    <!-- Title -->
    <mat-form-field class="w-full" appearance="outline">
      <mat-label>Titre de l'annonce</mat-label>
      <input
        matInput
        #titleInput
        placeholder="Ex: Magnifique appartement T3 avec vue mer"
        [formField]="formTree.title"
      />
      @if (hasContactInTitle()) {
        <mat-hint class="text-amber-11 font-medium"
          >⚠️ Coordonnées détectées : elles seront masquées.</mat-hint
        >
      }
      <mat-hint align="end">{{ (formTree.title().value() || '').length }} / 50</mat-hint>
    </mat-form-field>

    <!-- Description -->
    <mat-form-field class="w-full" appearance="outline">
      <mat-label>Description</mat-label>
      <textarea
        matInput
        #descInput
        class="min-h-[120px] resize-y"
        placeholder="Décrivez les points forts du bien, les travaux réalisés, le quartier..."
        [formField]="formTree.description"
      ></textarea>
      @if (hasContactInDescription()) {
        <mat-hint class="text-amber-11 font-medium"
          >⚠️ Coordonnées détectées : elles seront masquées.</mat-hint
        >
      }
      <mat-hint align="end">{{ (formTree.description().value() || '').length }} / 800</mat-hint>
    </mat-form-field>
  </div>
</div>
